import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Spacing} from 'types/Theme'

import {cssTheme} from '../../../../themes'
import {linariaMq} from '../../../../utils/breakpoints'

interface PageWidthContainerPropsType {
  /** The page content */
  children: React.ReactNode
  /** A class name that can be given to the container */
  className?: string
  /** The max width the page will be refined to. If not supplied it is taken from the theme file */
  maxPageWidth?: number
  /** Whether to span the container full width - this will override maxPageWidth */
  isFullWidth?: boolean
  /** The page padding at the desktop-sm breakpoint. Only spacing sizes from the theme are accepted */
  paddingDesktopSmSize?: Spacing
  /** The page padding at the desktop-xs breakpoint. Only spacing sizes from the theme are accepted */
  paddingDesktopXsSize?: Spacing
  /** The page padding at the desktop-md breakpoint. Only spacing sizes from the theme are accepted */
  paddingDesktopMdSize?: Spacing
  /** The page padding at the desktop-lg breakpoint. Only spacing sizes from the theme are accepted */
  paddingDesktopLgSize?: Spacing
  /** The page padding at the mobile-sm breakpoint. Only spacing sizes from the theme are accepted */
  paddingMobileSmSize?: Spacing
  /** The page padding at the mobile-lg breakpoint. Only spacing sizes from the theme are accepted */
  paddingMobileLgSize?: Spacing
}

type PageWidthContainerElementPropsType = Omit<
  PageWidthContainerPropsType,
  'children' | 'className'
>

const getPadding = (size?: Spacing) =>
  cssTheme.layout.spacing[(size || 's000') as Spacing]

const PageWidthContainerElement = styled.div<PageWidthContainerElementPropsType>`
  max-width: ${({isFullWidth, maxPageWidth}) =>
    isFullWidth
      ? '100%'
      : maxPageWidth
        ? `${maxPageWidth}px`
        : cssTheme.layout.maxPageWidth};
  margin: 0 auto;
  padding: 0 ${({paddingMobileSmSize}) => getPadding(paddingMobileSmSize)};

  &.--has-padding-mobile-lg-size {
    ${linariaMq.mobileLg} {
      padding: 0 ${({paddingMobileLgSize}) => getPadding(paddingMobileLgSize)};
    }
  }
  &.--has-padding-desktop-xs-size {
    ${linariaMq.desktopXs} {
      width: 100%;
      padding: 0 ${({paddingDesktopXsSize}) => getPadding(paddingDesktopXsSize)};
    }
  }
  &.--has-padding-desktop-sm-size {
    ${linariaMq.desktopSm} {
      padding: 0 ${({paddingDesktopSmSize}) => getPadding(paddingDesktopSmSize)};
    }
  }
  &.--has-padding-desktop-md-size {
    ${linariaMq.desktopMd} {
      padding: 0 ${({paddingDesktopMdSize}) => getPadding(paddingDesktopMdSize)};
    }
  }
  &.--has-padding-desktop-lg-size {
    ${linariaMq.desktopLg} {
      padding: 0 ${({paddingDesktopLgSize}) => getPadding(paddingDesktopLgSize)};
    }
  }
`

export const PageWidthContainer = ({
  children,
  className,
  isFullWidth,
  maxPageWidth,
  paddingDesktopLgSize,
  paddingDesktopMdSize,
  paddingDesktopSmSize,
  paddingDesktopXsSize,
  paddingMobileSmSize,
  paddingMobileLgSize
}: PageWidthContainerPropsType) => (
  <PageWidthContainerElement
    className={cx(
      className,
      paddingDesktopLgSize && '--has-padding-desktop-lg-size',
      paddingDesktopMdSize && '--has-padding-desktop-md-size',
      paddingDesktopSmSize && '--has-padding-desktop-sm-size',
      paddingDesktopXsSize && '--has-padding-desktop-xs-size',
      paddingMobileSmSize && '--has-padding-mobile-sm-size',
      paddingMobileLgSize && '--has-padding-mobile-lg-size'
    )}
    maxPageWidth={maxPageWidth}
    isFullWidth={isFullWidth}
    paddingDesktopLgSize={paddingDesktopLgSize}
    paddingDesktopMdSize={paddingDesktopMdSize}
    paddingDesktopSmSize={paddingDesktopSmSize}
    paddingDesktopXsSize={paddingDesktopXsSize}
    paddingMobileSmSize={paddingMobileSmSize}
    paddingMobileLgSize={paddingMobileLgSize}
  >
    {children}
  </PageWidthContainerElement>
)
